import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

const SEO = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  articleSection,
  tags = [],
  noindex = false,
  nofollow = false,
  canonical,
  structuredData,
  breadcrumbs
}) => {
  const location = useLocation();
  const siteUrl = 'https://www.finclify.com';
  const siteName = 'Finclify - Finance Tools & Calculators';
  const defaultImage = `${siteUrl}/images/og-image.jpg`;
  const currentUrl = canonical || `${siteUrl}${location.pathname}`;
  
  // Build full title
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  
  // Default description
  const defaultDescription = 'Calculate EMI, plan investments, compare loans and credit cards. Free financial planning tools for smart investments and wealth building.';
  const metaDescription = description || defaultDescription;
  
  // Keywords - use provided keywords, only fallback to default if not provided at all
  // This ensures each page has unique keywords
  const defaultKeywords = 'finance calculator, EMI calculator, investment calculator, loan calculator, tax calculator, SIP calculator, financial planning, credit card comparison';
  // Only use default if keywords is undefined or null (not if it's an empty string)
  const metaKeywords = (keywords !== undefined && keywords !== null && keywords !== '') 
    ? keywords 
    : defaultKeywords;
  
  // Meta image
  const metaImage = image || defaultImage;
  const absoluteImage = metaImage.startsWith('http') ? metaImage : `${siteUrl}${metaImage}`;
  
  // Robots meta
  const robotsContent = [];
  if (noindex) robotsContent.push('noindex');
  if (nofollow) robotsContent.push('nofollow');
  if (robotsContent.length === 0) robotsContent.push('index, follow');
  
  // Build breadcrumb structured data
  const breadcrumbStructuredData = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${siteUrl}${crumb.url}`
    }))
  } : null;
  
  // Build article structured data
  const articleStructuredData = type === 'article' ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: metaDescription,
    image: absoluteImage,
    datePublished: publishedTime,
    dateModified: modifiedTime || publishedTime,
    author: {
      '@type': 'Person',
      name: author || 'Finclify Team'
    },
    publisher: {
      '@type': 'Organization',
      name: siteName,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo512.svg`
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': currentUrl
    },
    articleSection: articleSection,
    keywords: metaKeywords
  } : null;
  
  // Build WebApplication structured data for calculators
  const webAppStructuredData = type === 'webapplication' ? {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: title,
    description: metaDescription,
    url: currentUrl,
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR'
    }
  } : null;
  
  // Combine all structured data
  const allStructuredData = [
    structuredData,
    breadcrumbStructuredData,
    articleStructuredData,
    webAppStructuredData
  ].filter(Boolean);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content={author || 'Finclify Team'} />
      <meta name="robots" content={robotsContent.join(', ')} />
      <link rel="canonical" href={currentUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title || fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Article specific Open Graph tags */}
      {type === 'article' && [
        author && <meta key="author" property="article:author" content={author} />,
        publishedTime && <meta key="published" property="article:published_time" content={publishedTime} />,
        modifiedTime && <meta key="modified" property="article:modified_time" content={modifiedTime} />,
        articleSection && <meta key="section" property="article:section" content={articleSection} />,
        ...tags.map((tag, index) => (
          <meta key={`tag-${index}`} property="article:tag" content={tag} />
        ))
      ].filter(Boolean)}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title || fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:site" content="@finclify" />
      <meta name="twitter:creator" content="@finclify" />
      
      {/* Additional Meta Tags */}
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      
      {/* Structured Data (JSON-LD) */}
      {allStructuredData.map((data, index) => (
        <script 
          key={index} 
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </Helmet>
  );
};

export default SEO;
